// Types for Growth › Campaigns (API_CONTRACT.md › Growth: campaigns;
// backend/src/routes/admin/platform-growth-campaigns.ts).

import type { ActivityStatus, AudienceVerify, Bucket, ISODateTime, LeadStatus, LeadType, VerifyState } from './types'

/**
 * One list of merge fields for every template (backend merge.ts › MERGE_FIELDS).
 * Each email fills what it can: a campaign the lead's, a one-off email the
 * person's, the league's and the signer's. A field it can't fill is a missing
 * value there; a campaign refuses CAMPAIGN_UNFILLED outright.
 */
export const MERGE_FIELDS = [
  'first_name', 'contact_name', 'league_name', 'sport', 'city', 'state', 'website', 'preview_url', 'site_url', 'signer_name', 'signer_title',
] as const
export type MergeField = (typeof MERGE_FIELDS)[number]
export const CAMPAIGN_UNFILLED: readonly MergeField[] = ['site_url', 'signer_name', 'signer_title']

export interface TemplateCheck {
  id: 'subject_honest' | 'sender_named' | 'fields_known' | 'campaign_fields' | 'unsubscribe' | 'one_link'
  label: string
  ok: boolean
  /** false: advice only, it doesn't stop a campaign. */
  required: boolean
  /** Only when a campaign uses the template. */
  campaign: boolean
  message: string
}

export interface TemplateReport {
  /** What every email needs: an honest subject and fields we know. */
  ok: boolean
  /** That and every campaign rule. A campaign can't start on a template without it. */
  campaign_ok: boolean
  checks: TemplateCheck[]
  fields: MergeField[]
  unknown_fields: string[]
}

export interface GrowthTemplate {
  id: number
  name: string
  subject: string
  body: string
  /** How many campaigns use it. */
  used_by: number
  check: TemplateReport
  created_at: ISODateTime
  updated_at: ISODateTime
}

export interface TemplateWrite {
  name?: string
  subject?: string
  body?: string
}

/** What every email gets after its body (Templates › Signature and footer). One setting for all templates. */
export interface EmailFooter {
  /** One-off emails, under the body. Only {{signer_name}} and {{signer_title}}. Empty: no signature. */
  signature: string
  /** The logo under every email's HTML part, when on. Off, or no URL: no logo. */
  logo_enabled: boolean
  logo_url: string
  logo_width: number
  /** Where the logo leads. Empty: nowhere. */
  logo_link: string
  /** A campaign email's line over the postal address and the unsubscribe link. Only {{league_name}}. Empty (the default): no line. */
  notice: string
  /** A campaign email's unsubscribe link (and its List-Unsubscribe headers). Off: neither. */
  unsubscribe_enabled: boolean
  /** The unsubscribe link's words. Default "Unsubscribe". */
  unsubscribe_text: string
}

export interface EmailFooterView {
  footer: EmailFooter
  defaults: EmailFooter
  /** GROWTH_MAILING_ADDRESS, set on the server; null when it isn't. */
  mailing_address: string | null
}

/** Templates › Follow-up after signup (services/growth/signup-follow-up.ts). Off until the owner turns it on. */
export interface FollowUpSettings {
  enabled: boolean
  /** When it was last turned on: only signups after this get one. */
  enabled_at: string | null
  template_id: number | null
  /** A signer's key (signers.ts). */
  signer: string
  /** How long after they sign up it goes, before the daytime rule: 15 minutes to 24 hours. */
  delay_minutes: number
}

export interface FollowUpView {
  settings: FollowUpSettings
  /** The template it sends: the one chosen, else the owner's "Thanks for signing up". Null when neither exists. */
  template: { id: number; name: string } | null
  /** Why it can't be turned on with that template, in words, or null. */
  problem: string | null
  /** When it goes: delay_minutes after signup (Ops sets it, between delay_min and delay_max), between day_start and day_end their time; without a time zone, from unknown_day_start in unknown_zone. */
  rule: { delay_minutes: number; delay_min: number; delay_max: number; day_start: number; day_end: number; unknown_zone: string; unknown_day_start: number }
  /** The email as a sample signup would get it. */
  sample: { subject: string; text: string } | null
  /** Follow-ups on the Schedule, not yet sent. */
  scheduled: number
}

/** Which email a template preview shows: a one-off (signed, with the logo) or a campaign's (with the marketing footer). */
export type PreviewAs = 'one_off' | 'campaign'

export interface TemplatePreview {
  subject: string
  text: string
  html: string
  /** Fields with no value and no fallback: a lead like this isn't sent the email. */
  missing: MergeField[]
  check: TemplateReport
}

export const CAMPAIGN_STATUSES = ['draft', 'scheduled', 'running', 'paused', 'done'] as const
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]

export interface CampaignAudience {
  /** Only new, researching and contacted. Default: new and researching. */
  statuses?: LeadStatus[]
  types?: LeadType[]
  states?: string[]
  platforms?: string[]
  sports?: string[]
  min_priority?: number
  q?: string
  /** Who by the activity check. Left out or empty means active leads only. */
  activity?: ActivityStatus[]
  /** Left out or empty means verified only. A failed lead can't be named. */
  verify?: AudienceVerify[]
  /** Linked platforms. Left out means any, and leads with none. */
  platform_ids?: number[]
  /** Left out or empty means every bucket but Skip. */
  buckets?: Bucket[]
  /** Leads picked by hand on the Leads page: only these, every other filter still applying to them. */
  lead_ids?: number[]
}

export interface CampaignStep {
  id: number
  step_number: number
  delay_days: number
  template_id: number
  template_name: string
}

/** No "delivered" and no "opened": SMTP and a mailbox can't tell us either. */
export interface CampaignStats {
  waiting: number
  sent: number
  dry_run: number
  clicked: number
  replied: number
  bounced: number
  unsubscribed: number
  skipped: number
  removed: number
  failed: number
}

export interface CampaignProblem {
  code: 'no_steps' | 'template_failed' | 'audience_empty'
  message: string
}

export interface Campaign {
  id: number
  name: string
  status: CampaignStatus
  audience: CampaignAudience
  daily_cap: number
  /** null: as soon as it starts. */
  starts_at: ISODateTime | null
  /** "09:00" */
  window_start: string
  window_end: string
  time_zone: string
  /** Why the system paused it. null when a person did. */
  paused_reason: string | null
  started_at: ISODateTime | null
  /** Who added it to the schedule: that was the approval of every email it plans. */
  started_by: { id: number; name: string | null; email: string } | null
  finished_at: ISODateTime | null
  created_at: ISODateTime
  updated_at: ISODateTime
  stats: CampaignStats
  steps: CampaignStep[]
  /** On the detail response: why a draft can't start yet. */
  problems?: CampaignProblem[]
}

export interface CampaignWrite {
  name?: string
  audience?: CampaignAudience
  daily_cap?: number
  starts_at?: ISODateTime | null
  window_start?: string
  window_end?: string
  time_zone?: string
  steps?: Array<{ template_id: number; delay_days?: number }>
}

export interface AudiencePreview {
  total: number
  eligible: number
  /** Who it left out, and why. `skip`, the activity counts and the verification counts are people who could otherwise be emailed. */
  excluded: { no_email: number; not_us: number; suppressed: number; undeliverable: number; skip: number; active: number; stale: number; unknown: number; needs_review: number; failed: number; unverified: number }
  /** Who's in, by activity. */
  included: Record<ActivityStatus, number>
  /** Who's in, by verification. */
  included_verify: Record<AudienceVerify, number>
  /** Who's in, by name, in the order they'd be emailed: the first 100. */
  sample: Array<{ id: number; name: string; email: string; city: string | null; state: string | null; priority: number; activity_status: ActivityStatus; verify_status: VerifyState; platform: string | null }>
}

export const SEND_STATUSES = ['pending_approval', 'queued', 'sending', 'sent', 'dry_run', 'skipped', 'removed', 'failed'] as const
export type SendStatus = (typeof SEND_STATUSES)[number]

export interface CampaignRecipient {
  id: number
  email: string
  status: SendStatus
  batch_date: string
  scheduled_for: ISODateTime | null
  sent_at: ISODateTime | null
  gate_reason: 'sending_off' | 'not_on_allowlist' | null
  skip_reason: string | null
  error: string | null
  subject: string | null
  clicked_at: ISODateTime | null
  replied_at: ISODateTime | null
  bounced_at: ISODateTime | null
  bounce_kind: 'hard' | 'soft' | null
  unsubscribed_at: ISODateTime | null
  step_number: number
  /** null once the lead is deleted. */
  lead: { id: number; name: string } | null
}

export interface RecipientListParams {
  status?: SendStatus
  page?: number
  page_size?: number
}

export interface RecipientListResponse {
  recipients: CampaignRecipient[]
  total: number
  page: number
  page_size: number
}

/** A reply that asked to stop. The address is already suppressed; this is for a person to read. On top of the Schedule. */
export interface ReviewItem {
  id: number
  email: string | null
  snippet: string | null
  received_at: ISODateTime | null
  created_at: ISODateTime
  lead: { id: number; name: string } | null
}

// ─── The Schedule ───────────────────────────────────────────────────────────

/** How an email on the Schedule stands. `scheduled` is waiting to go (a campaign row queued, a one-off scheduled). */
export const SCHEDULE_STATES = ['scheduled', 'sending', 'sent', 'dry_run', 'failed', 'removed', 'skipped'] as const
export type ScheduleState = (typeof SCHEDULE_STATES)[number]
export type ScheduleKind = 'campaign' | 'one_off'

/** "2026-11-03". */
export type ISODay = string

export interface ScheduleDay {
  day: ISODay
  scheduled: number
  sending: number
  sent: number
  dry_run: number
  failed: number
  removed: number
  skipped: number
  /** Of all those, how many were one-off emails. */
  one_off: number
}

export interface ScheduleResponse {
  /** The Ops zone: one-off emails sit on their day in it, and "today" is read in it. */
  time_zone: string
  today: ISODay
  from: ISODay
  to: ISODay
  /** Every day from `from` to `to`, counted. */
  days: ScheduleDay[]
  review: ReviewItem[]
}

export interface ScheduleEmail {
  kind: ScheduleKind
  id: number
  day: ISODay
  status: ScheduleState
  /** When it went, or is set to go. Null for a campaign email whose time is set on its day. */
  at: ISODateTime | null
  to: string
  /** The lead's name, or the league's for a signup's or a tenant's email. */
  recipient: string
  lead: { id: number; name: string } | null
  league: { id: number; name: string } | null
  signup_id: number | null
  campaign: { id: number; name: string; status: CampaignStatus } | null
  step_number: number | null
  template_name: string | null
  signer_name: string | null
  subject: string | null
  skip_reason: string | null
  gate_reason: 'sending_off' | 'not_on_allowlist' | null
  error: string | null
  /** A campaign email's sending window, for one with no time yet. */
  window: { start: string; end: string; time_zone: string } | null
  can_remove: boolean
  can_move: boolean
}

export interface ScheduleEmailsResponse {
  time_zone: string
  from: ISODay
  to: ISODay
  emails: ScheduleEmail[]
  /** More than 500 of a kind in the range: narrow it. */
  truncated: boolean
}

/** One email, opened: its subject and its whole body as it went, or as it would go. */
export interface ScheduleEmailDetail extends ScheduleEmail {
  from: string
  subject: string
  text: string
  /** as_sent: the text that went. as_it_would_go: a campaign email rendered now (it's written again at the send). as_it_will_go: a scheduled one-off, written already. */
  rendered: 'as_sent' | 'as_it_would_go' | 'as_it_will_go'
  /** Why it wouldn't go as it stands, in a sentence. */
  problem: string | null
  approved_at?: ISODateTime | null
  approved_by?: { id: number; name: string | null; email: string } | null
  sent_by?: { id: number; name: string | null; email: string } | null
  clicked_at?: ISODateTime | null
  replied_at: ISODateTime | null
  bounced_at: ISODateTime | null
  bounce_kind: 'hard' | 'soft' | null
  unsubscribed_at: ISODateTime | null
}

export interface SendingStatus {
  enabled: boolean
  /** dry_run: nothing leaves. test_list: only listed addresses. live: every eligible lead. */
  mode: 'dry_run' | 'test_list' | 'live'
  allowlist_count: number | null
  from: string
  smtp_configured: boolean
  imap_configured: boolean
  mailing_address: string | null
  problems: Array<{ code: string; message: string }>
  settings: { daily_cap: number; recontact_days: number; time_zone: string }
  today: { day: string; sent: number; dry_run: number; failed: number; waiting: number; cap: number; next_send_at: ISODateTime | null }
  rates: { window_days: number; sent: number; bounced: number; replied: number; unsubscribed: number; bounce_rate: number; bounce_limit: number }
  /** A mailbox gives no complaint signal. Always false, with where to look instead. */
  complaints: { visible: false; tools_url: string }
  inbox: { polled_at: ISODateTime | null; error: string | null }
}

export type SendingSettingsWrite = Partial<SendingStatus['settings']>
